import React, { useState, useEffect, useCallback } from 'react';
import {
  Container,
  Typography,
  Box,
  Paper,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  TextField,
  Button,
  Chip,
  Alert,
} from '@mui/material';
import { subscriptionApi, getCompanyId } from './subscriptionApi';

const company = JSON.parse(sessionStorage.getItem('companyDetails') || '{}');
const currency = company?.currency ?? '₹';
const fmtDate = (d) => (d ? new Date(d).toLocaleDateString('en-GB') : '—');

const statusChip = (s) => {
  const v = (s || '').toLowerCase();
  if (v === 'success')
    return <Chip label='Success' color='success' size='small' />;
  if (v === 'failed' || v === 'failure')
    return <Chip label='Failed' color='error' size='small' />;
  return <Chip label={s || '—'} size='small' />;
};

export default function SubscriptionPaymentReport() {
  const companyId = getCompanyId();
  const [rows, setRows] = useState([]);
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    if (!companyId) return;
    setLoading(true);
    try {
      const res = await subscriptionApi.getPaymentReport(
        companyId,
        from || undefined,
        to || undefined,
      );
      setRows(res.data || []);
    } catch (err) {
      console.error('Load subscription payment report failed', err);
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [companyId, from, to]);

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const totalPaid = rows
    .filter((r) => (r.paymentStatus || '').toLowerCase() === 'success')
    .reduce((sum, r) => sum + (Number(r.amount) || 0), 0);

  return (
    <Container maxWidth='lg' sx={{ mt: 4, mb: 6 }}>
      <Typography variant='h5' sx={{ fontWeight: 700, mb: 3 }}>
        Subscription Payments Report
      </Typography>

      {/* Filters */}
      <Paper sx={{ p: 2, mb: 3, borderRadius: 3, boxShadow: 2 }}>
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
          <TextField
            label='From'
            type='date'
            size='small'
            InputLabelProps={{ shrink: true }}
            value={from}
            onChange={(e) => setFrom(e.target.value)}
          />
          <TextField
            label='To'
            type='date'
            size='small'
            InputLabelProps={{ shrink: true }}
            value={to}
            onChange={(e) => setTo(e.target.value)}
          />
          <Button variant='contained' onClick={load} disabled={loading}>
            {loading ? 'Loading…' : 'Apply'}
          </Button>
          <Box sx={{ flexGrow: 1 }} />
          <Typography sx={{ fontWeight: 700 }}>
            Total Paid: {currency}
            {totalPaid.toFixed(2)}
          </Typography>
        </Box>
      </Paper>

      <Paper sx={{ borderRadius: 3, overflow: 'hidden', boxShadow: 2 }}>
        <Table size='small'>
          <TableHead>
            <TableRow sx={{ backgroundColor: '#f1f5f9' }}>
              <TableCell sx={{ fontWeight: 700 }}>#</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>Plan</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>Amount</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>Gateway</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>Transaction ID</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>Status</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>Paid On</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>Term</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.length === 0 && (
              <TableRow>
                <TableCell colSpan={8} align='center' sx={{ py: 4 }}>
                  {loading ? 'Loading…' : 'No subscription payments found.'}
                </TableCell>
              </TableRow>
            )}
            {rows.map((r, i) => (
              <TableRow key={r.paymentId} hover>
                <TableCell>{i + 1}</TableCell>
                <TableCell>
                  {r.planName || '—'}{' '}
                  {r.isTrial && <Chip label='Trial' color='info' size='small' />}
                </TableCell>
                <TableCell>
                  {currency}
                  {Number(r.amount).toFixed(2)}
                </TableCell>
                <TableCell>{r.paymentGateway || '—'}</TableCell>
                <TableCell>{r.transactionId || '—'}</TableCell>
                <TableCell>{statusChip(r.paymentStatus)}</TableCell>
                <TableCell>
                  {r.paymentDate
                    ? new Date(r.paymentDate).toLocaleString('en-GB')
                    : '—'}
                </TableCell>
                <TableCell>
                  {fmtDate(r.startDate)} – {fmtDate(r.endDate)}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Paper>

      {!companyId && (
        <Alert severity='warning' sx={{ mt: 2 }}>
          No company in session — please log in again.
        </Alert>
      )}
    </Container>
  );
}
